import { PropsWithChildren } from 'react';
import useSWR from 'swr';
import getFeatureFlag from 'fetchers/featureFlags/getFeatureFlag';
import LoadingSVG from 'components/common/LoadingSVG';
import { useAuth } from './AuthProvider';

export const NATURE_OF_BUSINESS = 'nature_of_business_enabled';
export const NEW_DASHBOARD_TOGGLE = 'new_dashboard_toggle_enabled';
export const DISPLAY_CALL_OPT_OUT = 'display_call_opt_out_enabled';
export const A4A_ADDITIONAL_SUPPORT = 'a4a_additional_support_enabled';
export const EDI_QUESTIONS_CONTROLLED_BY_DECEASED_FLAG = 'edi_questions_controlled_by_deceased_flag';
export const SUBMISSION_CAPTURE_PHONE_NUMBER_EARLY = 'submission_capture_phone_number_early';
export const GET_NEXT_VERSION_TWO_ENABLED = 'get_next_version_two_enabled';
export const HEAT_NETWORK_ISSUE_TYPE_ENABLED = 'heat_network_issue_type_enabled';
export const HEAT_NETWORK_ENABLED = 'heat_network_enabled';
export const OFFLINE_CONSUMER_CHIP = 'offline_consumer_chip_enabled';
export const FLEX_COMPANY_TYPE_ENABLED = 'flex_company_type_enabled';
export const EXPOSE_COMPANY_SITES_ENABLED = 'expose_company_sites_enabled';
export const EXPOSE_COMPANY_SITES_FOR_ADMIN_ENABLED = 'expose_company_sites_for_admin_enabled';
export const DISPLAY_COMPLAINT_AI_SUMMARY_ENABLED = 'display_complaint_ai_summary_enabled';
export const GENERATE_COMPLAINT_AI_SUMMARY_ENABLED = 'generate_complaint_ai_summary_enabled';
export const DISPLAY_COMPANY_ASSIGN_BUTTON_ENABLED = 'display_company_assign_button_enabled';
export const DISPLAY_COMPANY_SETTINGS_ENABLED = 'display_company_settings_enabled';
export const DISPLAY_SUPPLIER_COMPLETE_MESSAGE_TASK_BUTTON_ENABLED = 'display_supplier_complete_message_task_button_enabled';
export const DISPLAY_REDESIGNED_EVIDENCE_ENABLED = 'display_redesigned_evidence_enabled';
export const DISPLAY_REDESIGNED_EVIDENCE_VIEWER_ENABLED = 'display_redesigned_evidence_viewer_enabled';
export const DISPLAY_SUPPLIER_SIGNPOSTING_TASK_ENABLED = 'display_supplier_signposting_task_enabled';
export const BATCH_EVIDENCE_UPLOAD_ENABLED = 'batch_evidence_upload_enabled';
export const SIMPLIFIED_FILE_UPLOAD_ENABLED = 'simplified_file_upload_enabled';
/**
 * When enabled, the redesigned (batch) evidence upload modal hides category
 * selection entirely and uploads files with no category — categories are never
 * offered or required at upload time. Users still manage categories afterwards
 * on the evidence table (unchanged). Default false; AppConfig-backed.
 */
export const EVIDENCE_UPLOAD_HIDE_CATEGORY_ENABLED = 'evidence_upload_hide_category_enabled';
/**
 * @deprecated Use the per-section flags below (AI_PROOFREADING_ISSUES_ENABLED,
 * AI_PROOFREADING_CONCLUSION_ENABLED, AI_PROOFREADING_REMEDIES_ENABLED) instead.
 * Retained for the compat-shim window (CD-8211). Removed by the follow-up ticket
 * once the sectionless shim is deleted from the cms.
 */
export const AI_PROOFREADING_ENABLED = 'ai_proofreading_enabled';

/** Feature flag controlling the Proofread button on the Issues section (CD-8211). */
export const AI_PROOFREADING_ISSUES_ENABLED = 'ai_proofreading_issues_enabled';
/** Feature flag controlling the Proofread button on the Conclusion section (CD-8211). */
export const AI_PROOFREADING_CONCLUSION_ENABLED = 'ai_proofreading_conclusion_enabled';
/** Feature flag controlling the Proofread button on the Remedies section (CD-8211). */
export const AI_PROOFREADING_REMEDIES_ENABLED = 'ai_proofreading_remedies_enabled';
export const FLAG_EVIDENCE_ENABLED = 'flag_evidence_enabled';
export const DISPLAY_COMPLAINT_ADMIN_PAGE_ENABLED = 'display_complaint_admin_page_enabled';
export const DISPLAY_COMPLAINT_ADMIN_COMPLAINT_PAGE_ENABLED = 'display_complaint_admin_complaint_page_enabled';
export const DISPLAY_COMPLAINT_ADMIN_COMMENTS_PAGE_ENABLED = 'display_complaint_admin_comments_page_enabled';
export const DISPLAY_COMPLAINT_ADMIN_DECISIONS_PAGE_ENABLED = 'display_complaint_admin_decisions_page_enabled';
export const DISPLAY_COMPLAINT_ADMIN_APPEALS_PAGE_ENABLED = 'display_complaint_admin_appeals_page_enabled';
export const DISPLAY_COMPLAINT_ADMIN_TASKS_PAGE_ENABLED = 'display_complaint_admin_tasks_page_enabled';
export const DISPLAY_COMPLAINT_ADMIN_USER_DELETION_BUTTON_ENABLED = 'display_complaint_admin_user_deletion_button_enabled';
export const EVIDENCE_PARTY_ENABLED = 'evidence_party_enabled';
export const EVIDENCE_INTERNAL_LABEL_ENABLED = 'evidence_internal_label_enabled';
export const EVIDENCE_SEARCH_ENABLED = 'evidence_search_enabled';
export const EVIDENCE_QUICK_FILTERS_ENABLED = 'evidence_quick_filters_enabled';
export const EVIDENCE_FILTERS_V2_ENABLED = 'evidence_filters_v2_enabled';

/**
 * CD-8214 spike flag — enables word/character-level diff highlighting in the
 * ProofreadPanel comparison view. Independent of the per-section proofreading
 * flags (AI_PROOFREADING_ISSUES_ENABLED etc.) so the team can A/B compare with
 * proofreading on but diff highlighting off vs. both on. Defaults to false;
 * AppConfig provisioning is out of scope for the spike.
 */
export const AI_PROOFREADING_DIFF_HIGHLIGHT_ENABLED = 'ai_proofreading_diff_highlight_enabled';
export const EVIDENCE_FOLDERS_ENABLED = 'evidence_folders_enabled';
export const CARES_SUPPORT_WIDGET_ENABLED = 'cares_support_widget_enabled';
/** Feature flag controlling the evidence ZIP download feature (CD-8203). Default false; AppConfig-backed. */
export const EVIDENCE_BUNDLE_DOWNLOAD_ENABLED = 'evidence_bundle_download_enabled';

/**
 * CD-8338 — gates the template-driven remedy authoring UI per user, per sector.
 * Two independent flags: one for energy complaints, one for communications.
 * Both default false; enabled via AppConfig (CD-8340).
 * The gate picks the flag by the complaint's sector symbol at render time —
 * an unknown/missing sector always falls back to the legacy UI regardless of
 * flag state (scenario 12 precedence rule).
 * Changes apply on next UI load within the 5-minute SWR dedup window, no deploy required.
 */
export const DISPLAY_TEMPLATED_REMEDIES_ENERGY_ENABLED = 'display_templated_remedies_energy_enabled';
export const DISPLAY_TEMPLATED_REMEDIES_COMMUNICATIONS_ENABLED = 'display_templated_remedies_communications_enabled';

/**
 * CD-8464 — gates the guided-selection wizard independently of templated remedies,
 * per sector. Additive to the templated flags above: guided selection lives inside the
 * templated UI and feeds it a template, so the guided card only appears when the
 * sector's templated flag is also on. Both default false; enabled via AppConfig.
 */
export const DISPLAY_GUIDED_REMEDIES_ENERGY_ENABLED = 'display_guided_remedies_energy_enabled';
export const DISPLAY_GUIDED_REMEDIES_COMMUNICATIONS_ENABLED = 'display_guided_remedies_communications_enabled';

/**
 * CD-8464 — gates the `/admin/wizards` guided-selection wizard-structure admin
 * (list page, nav entry, and the flow editor). Independent of the officer-facing
 * wizard flags above. Default false; enabled via AppConfig.
 */
export const WIZARD_ADMIN_ENABLED = 'wizard_admin_enabled';

/** CD-8338-B — gates the `/admin/templates` template-admin UI (list, nav entry, editor). Default false; AppConfig-backed. */
export const TEMPLATE_ADMIN_ENABLED = 'template_admin_enabled';

/**
 * CD-8760 — gates the extended (worldwide) country list on Communications-sector
 * consumer/correspondence address forms. Combined with a per-company allowlist; see
 * lib/builders/complaints/countryList. Default false; AppConfig-backed.
 */
export const COMMUNICATIONS_OTHER_COUNTRY_ENABLED = 'communications_other_country_enabled';

export type FeatureConfig = Record<Feature, boolean>;
export const defaultFeatures = {
  [NATURE_OF_BUSINESS]: true,
  [NEW_DASHBOARD_TOGGLE]: false,
  [DISPLAY_CALL_OPT_OUT]: false,
  [EDI_QUESTIONS_CONTROLLED_BY_DECEASED_FLAG]: false,
  [A4A_ADDITIONAL_SUPPORT]: true, // TODO: Make this false by default
  [SUBMISSION_CAPTURE_PHONE_NUMBER_EARLY]: false,
  [GET_NEXT_VERSION_TWO_ENABLED]: false,
  [HEAT_NETWORK_ISSUE_TYPE_ENABLED]: false,
  [HEAT_NETWORK_ENABLED]: false,
  [OFFLINE_CONSUMER_CHIP]: false,
  [FLEX_COMPANY_TYPE_ENABLED]: false,
  [EXPOSE_COMPANY_SITES_ENABLED]: false,
  [EXPOSE_COMPANY_SITES_FOR_ADMIN_ENABLED]: false,
  [DISPLAY_COMPLAINT_AI_SUMMARY_ENABLED]: false,
  [GENERATE_COMPLAINT_AI_SUMMARY_ENABLED]: false,
  [DISPLAY_COMPANY_ASSIGN_BUTTON_ENABLED]: false,
  [DISPLAY_COMPANY_SETTINGS_ENABLED]: false,
  [DISPLAY_SUPPLIER_COMPLETE_MESSAGE_TASK_BUTTON_ENABLED]: false,
  [DISPLAY_REDESIGNED_EVIDENCE_ENABLED]: false,
  [DISPLAY_REDESIGNED_EVIDENCE_VIEWER_ENABLED]: false,
  [DISPLAY_SUPPLIER_SIGNPOSTING_TASK_ENABLED]: false,
  [BATCH_EVIDENCE_UPLOAD_ENABLED]: false,
  [SIMPLIFIED_FILE_UPLOAD_ENABLED]: false,
  [EVIDENCE_UPLOAD_HIDE_CATEGORY_ENABLED]: false,
  [AI_PROOFREADING_ENABLED]: false,
  [AI_PROOFREADING_ISSUES_ENABLED]: false,
  [AI_PROOFREADING_CONCLUSION_ENABLED]: false,
  [AI_PROOFREADING_REMEDIES_ENABLED]: false,
  [FLAG_EVIDENCE_ENABLED]: false,
  [DISPLAY_COMPLAINT_ADMIN_PAGE_ENABLED]: false,
  [DISPLAY_COMPLAINT_ADMIN_COMPLAINT_PAGE_ENABLED]: false,
  [DISPLAY_COMPLAINT_ADMIN_COMMENTS_PAGE_ENABLED]: false,
  [DISPLAY_COMPLAINT_ADMIN_DECISIONS_PAGE_ENABLED]: false,
  [DISPLAY_COMPLAINT_ADMIN_APPEALS_PAGE_ENABLED]: false,
  [DISPLAY_COMPLAINT_ADMIN_TASKS_PAGE_ENABLED]: false,
  [DISPLAY_COMPLAINT_ADMIN_USER_DELETION_BUTTON_ENABLED]: false,
  [EVIDENCE_PARTY_ENABLED]: false,
  [EVIDENCE_INTERNAL_LABEL_ENABLED]: false,
  [EVIDENCE_SEARCH_ENABLED]: false,
  [EVIDENCE_QUICK_FILTERS_ENABLED]: false,
  [EVIDENCE_FILTERS_V2_ENABLED]: false,
  [EVIDENCE_FOLDERS_ENABLED]: false,
  [EVIDENCE_BUNDLE_DOWNLOAD_ENABLED]: false,
  [AI_PROOFREADING_DIFF_HIGHLIGHT_ENABLED]: false,
  [CARES_SUPPORT_WIDGET_ENABLED]: false,
  [DISPLAY_TEMPLATED_REMEDIES_ENERGY_ENABLED]: false,
  [DISPLAY_TEMPLATED_REMEDIES_COMMUNICATIONS_ENABLED]: false,
  [DISPLAY_GUIDED_REMEDIES_ENERGY_ENABLED]: false,
  [DISPLAY_GUIDED_REMEDIES_COMMUNICATIONS_ENABLED]: false,
  [WIZARD_ADMIN_ENABLED]: false,
  [TEMPLATE_ADMIN_ENABLED]: false,
  [COMMUNICATIONS_OTHER_COUNTRY_ENABLED]: false,
};

/**
 * CD-8740: the `withCredentials` parameter is gone. The cms axios instance sends
 * credentials on every request, so there is no longer a per-caller choice to
 * make — and the choice that used to be made here was the only place in the SPA
 * that treated the two auth modes as different kinds of request.
 */
export async function getFeatureValues(defaultValues: FeatureConfig) {
  const features = { ...defaultValues };

  const requests = Object.entries(defaultValues).map(
    ([feature, defaultValue]) =>
      new Promise((resolve) => {
        getFeatureFlag(feature, defaultValue)
          .then((value) => resolve({ [feature]: value }))
          // getFeatureFlag swallows its own errors today, but a rejection that
          // escaped would leave this promise pending forever and strand every
          // signed-in user on the FeatureProvider spinner.
          .catch(() => resolve({ [feature]: defaultValue }));
      }),
  );

  const featuresArray = await Promise.all(requests);
  for (const obj of featuresArray) {
    Object.assign(features, obj);
  }

  return features;
}

// Hold features in memory
type IFeatureStore = {
  [key in Feature]: boolean;
};
let featureStore: IFeatureStore = { ...defaultFeatures } as IFeatureStore;

export const FeatureProvider = ({ children }: PropsWithChildren) => {
  // CD-8740: one auth model. Previously this branched on a raw
  // `localStorage.getItem('oso-auth')` truthiness check OR the referral context;
  // both are now behind `useAuth()`, and `mode` (rather than a boolean derived
  // from which store answered) keys the cache so flags do not leak across a
  // referral -> standard transition.
  const { user, mode } = useAuth();
  const authorized = Boolean(user);
  const cacheKey = authorized ? ['feature-flags', mode] : null;

  const { data: features, isLoading } = useSWR<FeatureConfig>(cacheKey, () => getFeatureValues(defaultFeatures), {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 300000, // 5 minutes - features don't change often
    errorRetryCount: 1,
    errorRetryInterval: 1000,
    fallbackData: defaultFeatures,
  });

  // Update the legacy feature store for backward compatibility
  featureStore = (features || defaultFeatures) as IFeatureStore;

  if (!authorized) {
    return children;
  }

  if (isLoading) {
    return (
      <div className="container mt-10">
        <LoadingSVG size={32} delay={500} />
      </div>
    );
  }

  return children;
};

// Features hook, can be used in functional components
type Feature = keyof typeof defaultFeatures;
export function getFeature(featureName: Feature) {
  return featureStore[featureName];
}

// To be used in tests to mock the value of a feature
export function mockFeature(featureName: Feature, value: boolean) {
  featureStore[featureName] = value;
}
